"use client";

import PaymentMethods from "@/app/components/PaymentMethods";
import PlanComparisonTable from "@/app/components/PlanComparisonTable";
import PricingCard from "@/app/components/PricingCard";
import { getImageUrl } from "@/lib/getImageUrl";
import { getValidInviteCode } from "@/lib/inviteCode";
import { PlanCode, PlanStripeLookupKey } from "@/types/subscription";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { ArrowIcon, ExchangeIcon, StackIcon } from "./icons/MembershipIcons";
import { getNextUpgradePlan, isPlanDowngrade } from "./PlanCommon";

interface InviteCodeValidation {
  isValid: boolean;
  discount?: number;
}

interface PricingPlanProps {
  currentPlanCode: PlanCode | undefined;
  onPaymentComplete: (planCode: PlanStripeLookupKey) => void;
}

export default function PricingPlan({ currentPlanCode, onPaymentComplete }: PricingPlanProps) {
  const theme = useTheme();
  const t = useTranslations("Membership");
  const inviteCodeRef = useRef<HTMLDivElement>(null);
  const paymentSectionRef = useRef<HTMLDivElement>(null);

  const [selectedPlan, setSelectedPlan] = useState<PlanCode>(getNextUpgradePlan(currentPlanCode));
  const [inviteCodeInput, setInviteCodeInput] = useState("");
  const [inviteCodeValidation, setInviteCodeValidation] = useState<InviteCodeValidation | null>(null);
  const [isValidatingCode, setIsValidatingCode] = useState(false);

  const handlePlanClick = (planCode: PlanCode) => {
    setSelectedPlan(planCode);
    if (paymentSectionRef.current) {
      paymentSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  // 处理邀请码验证
  const handleInviteCodeValidation = async () => {
    if (!inviteCodeInput.trim()) {
      return;
    }

    setIsValidatingCode(true);
    setInviteCodeValidation(null);

    try {
      const result = await getValidInviteCode(inviteCodeInput.trim());
      if (result.success && result.data) {
        setInviteCodeValidation({
          isValid: true,
          discount: result.data.discount,
        });
      } else {
        setInviteCodeValidation({
          isValid: false,
        });
      }
    } catch (error) {
      setInviteCodeValidation({
        isValid: false,
      });
    } finally {
      setIsValidatingCode(false);
    }
  };

  const features: string[] = t.raw("header.features");
  const headIcons: any[] = [<ArrowIcon />, <StackIcon />, <ExchangeIcon />];
  const ExplorerFeatures: string[] = t.raw("pricing.explorer.features");
  const VisionaryFeatures: string[] = t.raw("pricing.visionary.features");
  const PrimeFeatures: string[] = t.raw("pricing.prime.features");

  // 定义卡片配置，避免重复
  const pricingCards = [
    {
      planCode: PlanCode.EXPLORER,
      title: t("pricing.explorer.title"),
      subtitle: t("pricing.explorer.subtitle"),
      price: 49.99,
      period: t("pricing.explorer.period"),
      features: ExplorerFeatures,
      label: t("pricing.explorer.label"),
      labelBgColor: "rgba(224, 83, 87, 0.30)",
      labelColor: "#E05357",
    },
    {
      planCode: PlanCode.VISIONARY,
      title: t("pricing.visionary.title"),
      subtitle: t("pricing.visionary.subtitle"),
      price: 488,
      period: t("pricing.visionary.period"),
      features: VisionaryFeatures,
      label: t("pricing.visionary.label"),
      labelBgColor: "rgba(19, 158, 121, 0.30)",
      labelColor: "#139E79",
    },
    {
      planCode: PlanCode.PRIME,
      title: t("pricing.prime.title"),
      subtitle: t("pricing.prime.subtitle"),
      price: 10000,
      period: "",
      features: PrimeFeatures,
      label: t("pricing.prime.label"),
      labelBgColor: "rgba(204, 158, 90, 0.30)",
      labelColor: "#CC9E5A",
    },
  ];

  // 渲染单个卡片的函数
  const renderPricingCard = (cardConfig: (typeof pricingCards)[0], key?: string) => {
    // Prime 计划不打折，其他计划根据邀请码打折
    const discount = cardConfig.planCode === PlanCode.PRIME ? 1 : inviteCodeValidation?.discount || 1;
    let finalPrice = cardConfig.price * discount;
    if (cardConfig.planCode === PlanCode.VISIONARY) {
      finalPrice = Math.round(finalPrice);
    }

    // 格式化价格：如果小数点后都是零，则不显示小数点和零
    const formattedPrice = finalPrice % 1 === 0 ? finalPrice.toFixed(0) : finalPrice.toFixed(2);

    return (
      <PricingCard
        key={key || cardConfig.planCode}
        title={cardConfig.title}
        subtitle={cardConfig.subtitle}
        price={formattedPrice}
        originalPrice={cardConfig.price.toString()}
        period={cardConfig.period}
        features={cardConfig.features}
        label={cardConfig.label}
        labelBgColor={cardConfig.labelBgColor}
        labelColor={cardConfig.labelColor}
        isSelected={selectedPlan === cardConfig.planCode}
        disabled={isPlanDowngrade(currentPlanCode, cardConfig.planCode)}
        buttonText={
          currentPlanCode === cardConfig.planCode ? t("pricing.buttons.currentPlan") : t("pricing.buttons.upgrade")
        }
        onClick={() => {
          if (!isPlanDowngrade(currentPlanCode, cardConfig.planCode)) {
            setSelectedPlan(cardConfig.planCode);
            if (inviteCodeRef.current) {
              inviteCodeRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        backgroundImage: `url(${getImageUrl("membership-banner-bg.png", theme.palette.mode)})`,
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
      }}>
      {/* Hero Section */}
      <Box textAlign="center" mb={{ xs: 6, md: 10 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: "var(--font-size-3xl)",
            fontWeight: "600",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(90deg,  #FFFAF5 0%, #E7C5A1 100%)"
                : "linear-gradient(90deg,  #E7C5A1 0%, #A27D49 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            lineHeight: 1.3,
            pt: { xs: "2rem", md: "4.5rem" }, // 手机端减小顶部内边距
          }}>
          {t.rich("header.title", { br: () => <br /> })}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "center",
              alignItems: { xs: "flex-start", md: "center" }, // 小屏幕时也居中对齐
              gap: { xs: 2, md: 3 },
              mt: { xs: 5, md: 3 },
              mb: 8,
              flexWrap: "wrap",
            }}>
            {features.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}>
                <Box>{headIcons[index % headIcons.length]}</Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                  }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Pricing Cards */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          alignItems: { xs: "stretch", sm: "center", md: "stretch" },
          justifyContent: "center",
          gap: { xs: 2, sm: 3, md: 3 },
        }}>
        {/* xs: 垂直布局 */}
        <Box
          sx={{
            display: { xs: "contents", sm: "none" },
          }}>
          {pricingCards.map((card) => renderPricingCard(card, `xs-${card.planCode}`))}
        </Box>

        {/* sm: 第一个卡片独占一行，第二、三个共用一行 */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex", md: "none" },
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            width: "100%",
          }}>
          {/* 第一行：Explorer */}
          <Box sx={{ width: "100%" }}>{renderPricingCard(pricingCards[0], `sm-${pricingCards[0].planCode}`)}</Box>

          {/* 第二行：Visionary 和 Prime */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              width: "100%",
              justifyContent: "center",
            }}>
            {renderPricingCard(pricingCards[1], `sm-${pricingCards[1].planCode}`)}
            {renderPricingCard(pricingCards[2], `sm-${pricingCards[2].planCode}`)}
          </Box>
        </Box>

        {/* md/lg: 一行三个卡片 */}
        <Box
          sx={{
            display: { xs: "none", sm: "none", md: "contents" },
          }}>
          {pricingCards.map((card) => renderPricingCard(card, `md-${card.planCode}`))}
        </Box>
      </Box>

      {/* Disclaimer Text */}
      <Box textAlign="center" sx={{ mt: { xs: "1.5rem", md: "2.63rem" } }}>
        <Typography
          variant="body1"
          color="text.tertiary"
          sx={{
            fontSize: "var(--font-size-xs)",
            lineHeight: 1.6,
          }}>
          {t.rich("disclaimer", { br: () => <br /> })}
        </Typography>
      </Box>

      {/* Invite Code Section */}
      <Box
        ref={inviteCodeRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 3,
          mt: { xs: "2rem", md: "3.63rem" },
          mb: "5.19rem",
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "center",
            alignItems: "center",
            gap: 3,
            width: "100%",
          }}>
          <TextField
            placeholder={t("inviteCode.placeholder")}
            variant="outlined"
            size="medium"
            value={inviteCodeInput}
            onChange={(e) => setInviteCodeInput(e.target.value)}
            error={inviteCodeValidation?.isValid === false}
            sx={{
              width: { xs: "100%", md: "25.25rem" }, // 手机端全宽
              maxWidth: { xs: "none", md: "25.25rem" },
              minWidth: { xs: "auto", md: "25.25rem" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "0.5rem",
                border: `1px solid ${theme.palette.mode === "dark" ? "transparent" : theme.palette.divider}`,
                color: "text.primary",
                fontSize: "1rem",
                height: "3.375rem",
              },
              "& .MuiInputBase-input": {
                px: "0.75rem",
                "&::placeholder": {
                  color: "text.secondary",
                  fontSize: "var(--font-size-xs)",
                  lineHeight: 1.6,
                  p: 0,
                },
              },
            }}
          />
          <Button
            variant="contained"
            color="tertiary"
            size="large"
            onClick={handleInviteCodeValidation}
            disabled={isValidatingCode || !inviteCodeInput.trim()}
            sx={{
              width: { xs: "100%", md: "10rem" }, // 手机端全宽
              height: "3.375rem",
              fontWeight: "600",
            }}>
            {isValidatingCode ? <CircularProgress size={20} color="inherit" /> : t("inviteCode.button")}
          </Button>
        </Box>

        {/* Validation Message */}
        {inviteCodeValidation && (
          <Typography variant="body2" color={inviteCodeValidation.isValid ? "success.main" : "error.main"}>
            {t(inviteCodeValidation.isValid ? "inviteCode.valid" : "inviteCode.invalid")}
          </Typography>
        )}
      </Box>

      {/* Payment Section */}
      <Box ref={paymentSectionRef}>
        <PaymentMethods
          selectedPlan={selectedPlan}
          onPaymentComplete={onPaymentComplete}
          inviteCode={inviteCodeValidation?.isValid ? inviteCodeInput : undefined}
        />
      </Box>

      {/* Comparison Table */}
      <PlanComparisonTable currentPlanCode={currentPlanCode} onPlanClick={handlePlanClick} />
    </Box>
  );
}
